import { TranslateFunc } from '@ha/intl';
import { UtilityBillsValues } from 'ha/models/Listing/types';
import { FacilitiesValues } from 'ha/models/ListingFacilities/constants';
import {
  FacilitiesDetailsTranslations,
  FacilitiesTranslations,
} from 'ha/models/ListingFacilities/translations';
import { getUtilityBillsFromCosts } from 'ha/models/ListingUtilityBills/getUtilityBillsFromCosts';
import { UtilityBillsLabelByValue } from 'ha/models/ListingUtilityBills/translations';

import {
  ListingCostsCosts,
  ListingFacilities,
  ListingKind,
} from '@ha/api/v2/types/Listing';
import { getAreaLabel } from 'ha/helpers/unitConverter';

export interface OGDescriptionArguments {
  T: TranslateFunc;
  kind: ListingKind;
  costs: ListingCostsCosts;
  freePlaces: number;
  facilities: ListingFacilities;
  listingDescription: string;
  isImperialSystem: boolean;
}

function getFacilityLabel<F extends keyof typeof FacilitiesDetailsTranslations>(
  T: TranslateFunc,
  facilities: ListingFacilities,
  facility: F,
): string | null {
  const key = facilities[facility];
  if (!key || key === FacilitiesValues.No) return null;
  const translation = FacilitiesDetailsTranslations[facility];
  return T(translation[key as any]);
}

export function getOGDescription({
  T,
  kind,
  costs,
  freePlaces,
  facilities,
  listingDescription,
  isImperialSystem,
}: OGDescriptionArguments): string {
  const billsIncludedLabel =
    getUtilityBillsFromCosts(costs) === UtilityBillsValues.INCLUDED
      ? T(UtilityBillsLabelByValue[UtilityBillsValues.INCLUDED])
      : null;
  const area =
    kind === ListingKind.EntirePlace
      ? facilities.total_size
      : facilities.bedroom_size;
  const areaLabel = area ? getAreaLabel(area, isImperialSystem) : null;
  const spaceLabel =
    freePlaces === 1
      ? T('Space for 1 person')
      : T('Space for %s persons', freePlaces);
  const wifiLabel = getFacilityLabel(T, facilities, 'wifi');
  const deskLabel =
    facilities.desk === FacilitiesValues.Yes
      ? T(FacilitiesTranslations.desk)
      : null;
  const airConditioningLabel = getFacilityLabel(T, facilities, 'ac');
  const balconyLabel = getFacilityLabel(T, facilities, 'balcony_terrace');
  const heatingLabel = getFacilityLabel(T, facilities, 'heating');
  const dishwasherLabel = getFacilityLabel(T, facilities, 'dishwasher');
  const parkingLabel = getFacilityLabel(T, facilities, 'parking');
  const petsAllowedLabel = getFacilityLabel(T, facilities, 'animal_allowed');
  const tvLabel = getFacilityLabel(T, facilities, 'tv');
  const washingMachineLabel = getFacilityLabel(
    T,
    facilities,
    'washing_machine',
  );
  const dryerLabel = getFacilityLabel(T, facilities, 'dryer');

  const benefits = (
    [
      billsIncludedLabel,
      areaLabel,
      spaceLabel,
      wifiLabel,
      deskLabel,
      airConditioningLabel,
      balconyLabel,
      heatingLabel,
      dishwasherLabel,
      parkingLabel,
      petsAllowedLabel,
      tvLabel,
      washingMachineLabel,
      dryerLabel,
    ].filter(e => e !== null) as string[]
  ).slice(0, 3);

  if (benefits.length < 3) return listingDescription;
  const [facility1, facility2, facility3] = benefits;

  return T('og.ldp.description', {
    facility1,
    facility2,
    facility3,
  });
}
